import { useGetProcessQuery } from "@/services/backend/processes/process/service";
import ConfirmationDialog from "@/shared/components/domain/confirmation/confirmation-dialog";
import { RTKQueryErrorAlert } from "@/shared/components/domain/errors/rtk-query-error-alert";
import {
  useErrArtefactNotReady,
  useSetErrArtefactNotReadyFromRtk,
} from "@/shared/service-manager/artefact/err-artefact-not-ready";
import { Button } from "@/shared/components/ui/button";
import { ReactElement, useEffect } from "react";
import { useCreateDocumentationMutation } from "@/services/backend/documentations/service";
import { v4 } from "uuid";
import { useAuth } from "@/shared/lib/authorization/auth-context";
import { useNavigate } from "react-router";

interface CreateDocumentationButtonProps {
  processId: string;
  customerId: string;
  deliveryId: string;
  disabled?: boolean;
}

export default function CreateDocumentationButton({
  processId,
  customerId,
  deliveryId,
  disabled = false,
}: CreateDocumentationButtonProps): ReactElement {
  const nav = useNavigate();
  const [createDocumentation, { data, error, reset, isLoading, isSuccess }] =
    useCreateDocumentationMutation();
  const { resetNotReadyError } = useErrArtefactNotReady();
  useSetErrArtefactNotReadyFromRtk(error, () => {
    reset();
  });

  const { user } = useAuth();

  useEffect(() => {
    if (isSuccess && data != null) {
      nav(`/processes/${processId}/documentation/${data.documentation.id}`);
    }
  }, [isSuccess, data, nav, processId]);

  const {
    data: process,
    isLoading: processIsLoading,
    error: processError,
  } = useGetProcessQuery({
    id: processId ?? "",
  });

  if (processIsLoading) {
    return <div />;
  }

  if (processError) {
    return <RTKQueryErrorAlert error={error} />;
  }

  const onConfirm = () => {
    if (isLoading) {
      return;
    }

    if (!process) {
      return;
    }

    createDocumentation({
      id: v4(),
      customerId,
      processId,
      deliveryId,
      createdBy: user!.id,
    });

    resetNotReadyError();
  };

  return (
    <ConfirmationDialog
      trigger={<Button disabled={disabled}>Probendokumentation starten</Button>}
      title="Probendokumentation erstellen"
      description="Hiermit wird die Dokumentation gestartet"
      onConfirm={onConfirm}
    />
  );
}
